import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';

interface User {
  email: string;
  name: string;
  picture?: string;
  sub: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isUnauthorized: boolean;
  isLoading: boolean;
  login: (credential: string) => Promise<void>;
  logout: () => void;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ALLOWED_DOMAIN = process.env.REACT_APP_ALLOWED_COMPANY_DOMAIN 
  ? `@${process.env.REACT_APP_ALLOWED_COMPANY_DOMAIN}` 
  : null; // null means allow all domains (for development)

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const storedToken = localStorage.getItem('google_token');
    if (storedToken) {
      try {
        const decoded = jwtDecode<User>(storedToken);
        // Check if token is expired
        const now = Date.now() / 1000;
        if ((decoded as any).exp && (decoded as any).exp > now) {
          // Validate domain if configured
          if (!ALLOWED_DOMAIN || decoded.email.endsWith(ALLOWED_DOMAIN)) {
            setUser(decoded);
            setIsUnauthorized(false);
          } else {
            setUser(decoded);
            setIsUnauthorized(true);
            setError(`Access denied. Only ${ALLOWED_DOMAIN} emails are allowed.`);
          }
        } else {
          localStorage.removeItem('google_token');
        }
      } catch (err) {
        localStorage.removeItem('google_token');
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (credential: string) => {
    setError(null);
    try {
      const decoded = jwtDecode<User>(credential);
      
      // Store token and user info
      localStorage.setItem('google_token', credential);
      setUser(decoded);
      
      // Validate email domain if configured
      if (ALLOWED_DOMAIN && !decoded.email.endsWith(ALLOWED_DOMAIN)) {
        setIsUnauthorized(true);
        setError(`Access denied. Only ${ALLOWED_DOMAIN} emails are allowed.`);
      } else {
        setIsUnauthorized(false);
      }
    } catch (err) {
      setError('Failed to authenticate. Please try again.');
    }
  };

  const logout = () => {
    localStorage.removeItem('google_token');
    setUser(null);
    setIsUnauthorized(false);
    setError(null);
  };

  return (
    <AuthContext.Provider 
      value={{
        user,
        isAuthenticated: !!user && !isUnauthorized,
        isUnauthorized,
        isLoading,
        login,
        logout,
        error
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};